import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {ObLoading} from '../ObLoading';
import {ArrowUUpLeft} from '../../icons';
import {Spinner} from '../../Spinner';
import {BudgetRow} from './BudgetRow';
import {fetchAgencyData, prepareBudgets} from '../api';
import {resolvedBudgetsCountOf} from '../budgetMath';
import {paceChoicesOf, paceNoticeCountOf} from '../pace';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {clientGroupsOf, dismissedAtOf, includedGroupsOf, withDismissedInPlace} from '../clientGroups';
import {EVENTS_UNAVAILABLE, choosesGoogleConversion, conversionFor, needsConversion} from '../conversionSuggestion';
import {useOnboarding} from '../wizardContext';
import {planQuotaUsage} from '../planQuota';
import {QuotaNote} from './QuotaNote';
import {attributeHelpCenterClick, prefetchHelpCenterIdentity} from '../../../utils/HelpCenterAttribution';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

// Cuánto se espera al catálogo antes de dejar de prometer que va a llegar. Las cuentas que sigan sin
// dato a esa altura ya se muestran como lo que son: líneas que nacen sin presupuesto.
const MAX_PREPARE_MS = 90000;
// Marca de dónde salió un descarte, para que el cajón de deshacer de este paso muestre lo que el
// usuario sacó ACÁ y no lo del paso 3 ni lo que apartó el sistema.
const DISMISSED_AT = 'budgets';
const DEFAULT_RETRY_S = 10;

// Lo que el encabezado ya no explica (H-02) vive en la ayuda: de dónde sale cada monto, y cómo se
// elige la conversión que se propone. Los artículos 60 y 59 del Centro de Ayuda.
export const HELP_BUDGETS_URL = 'https://ayuda.dashbo.io/es/articles/a-60-que-pasa-con-el-presupuesto-que-sobra-o-falta';
export const HELP_CONVERSION_URL = 'https://ayuda.dashbo.io/es/articles/a-59-elige-que-conversion-monitorea-cada-cuenta';

/**
 * Paso 4 — los presupuestos, sin tipear.
 *
 * Cada línea nace con lo que la cuenta gastó el mes pasado como punto de partida, y el gasto crudo
 * queda a la vista al lado del monto: es lo que sostiene la confianza en un número redondeado que
 * el usuario no calculó. Aceptar todo sin tocar nada es un final válido — el presupuesto se afina
 * después, y una cartera monitoreada con montos aproximados vale más que una sin monitorear.
 */
export function StepBudgets() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {state, dispatch, server, loadBudgets, loadProposals, loadConversionEvents,
           loadAccountActions} = useOnboarding();
    // El mismo cupo que el paso 3: si la cartera llegó hasta acá pasada del plan, «Crear» no sale.
    const quota = useMemo(() => planQuotaUsage({decisions: state.decisions, candidates: server.candidates,
                                                quota: server.proposals?.plan_quota}),
                          [state.decisions, server.candidates, server.proposals]);
    const [loaded, setLoaded] = useState(false);
    const [partial, setPartial] = useState(false);
    // El catálogo de campañas todavía viene en camino: las filas sin monto no son definitivas.
    const [preparing, setPreparing] = useState(false);
    const [ready, setReady] = useState(Boolean(server.candidates));
    const [agency, setAgency] = useState(null);
    // Todas las tandas se cayeron: no sabemos NADA de ninguna cuenta. Es un estado propio y no un
    // caso de `partial` —que dice «algunas»— porque lo que hay que mostrar es un error, no filas.
    const [failed, setFailed] = useState(false);
    const [retries, setRetries] = useState(0);

    // Si se llega acá recargando o por enlace directo, no hay nada en memoria: las cuentas se
    // vuelven a pedir antes de poder mostrar un solo monto.
    useEffect(() => {
        if (ready) return;
        loadProposals().then(() => setReady(true));
    }, [ready, loadProposals]);

    // Sólo por la moneda: si la agencia no tiene el módulo multi-moneda, el selector no se ofrece.
    useEffect(() => {
        fetchAgencyData().then(response => {
            if (response?.status === 'OK') setAgency(response.general_data || null);
        });
    }, []);

    const groups = useMemo(() => clientGroupsOf(state, server.candidates), [state, server.candidates]);
    const included = useMemo(() => includedGroupsOf(groups.assigned), [groups]);
    // Lo que el usuario sacó DESDE ESTE PASO. Sale de las decisiones guardadas y no de un estado del
    // componente: así sobrevive a una recarga, igual que sobrevive el descarte. No es el cajón del
    // paso 3 —eso lo separa `dismissedAt`— ni lo que descarta el sistema por cuenta muerta.
    const dismissedHere = useMemo(() => dismissedAtOf(groups, DISMISSED_AT), [groups]);
    const rows = useMemo(() => withDismissedInPlace(included, dismissedHere), [included, dismissedHere]);
    // El cliente que se acaba de sacar, para llevarle el foco a su deshacer (ver la tabla).
    const [justDismissed, setJustDismissed] = useState(null);

    const pks = useMemo(() => included.flatMap(group => group.accounts.map(entry => entry.pk)), [included]);

    /**
     * Pedir el catálogo primero, después los montos, y volver a preguntar mientras se esté trayendo.
     *
     * Las dos fuentes del presupuesto sugerido son el gasto del mes pasado y el diario configurado en
     * la plataforma. La segunda sale del catálogo de campañas, que **no se trae al conectar la
     * cuenta**: hay que pedirlo. Sin ese POST —que nadie hacía— la fuente `PLATFORM_BUDGET` no podía
     * existir, y como en el minuto cero del túnel tampoco hay gasto sincronizado, la enorme mayoría
     * de las filas nacía sin ninguna sugerencia.
     *
     * El poll existe porque el catálogo tarda: el GET dice en `refresh.pending` si vale la pena
     * volver a preguntar y en `retry_after_seconds` cada cuánto. Cuando se apaga, lo que quedó sin
     * monto es definitivo.
     */
    useEffect(() => {
        if (!ready) return undefined;
        // Sin cuentas no hay nada que pedir, y tampoco puede quedar un error de antes: si el usuario
        // sacó TODOS los clientes mientras el poll seguía y una vuelta falló, «Reintentar» llega acá con
        // la lista vacía. Sin limpiar `failed`, el paso quedaba trabado: sin tabla, sin el vacío y sin
        // el «Deshacer» de lo que sacó (review C-rp-fe-alta-02).
        if (!pks.length) {
            setFailed(false);
            setPreparing(false);
            setLoaded(true);
            return undefined;
        }

        let alive = true;
        let timer = null;
        const startedAt = Date.now();

        const ask = () => {
            // Cancela la vuelta agendada: `ask` se dispara desde dos lados (el primer pedido y la
            // vuelta del POST) y sin esto quedarían dos cadenas de poll corriendo en paralelo.
            clearTimeout(timer);
            loadBudgets(pks).then(result => {
                if (!alive) return;
                // Con TODO caído no hay ningún monto, y pintar eso como «nace sin presupuesto» es
                // convertir un error nuestro en un hecho sobre las cuentas del usuario —que además
                // se lleva a la cartera, porque desde acá se crea—. No se marca `loaded`: sin datos
                // no hay filas que mostrar ni botón que habilitar, y queda el reintento.
                if (!result) {
                    setFailed(true);
                    setPreparing(false);
                    return;
                }
                setFailed(false);
                // Los presupuestos se piden por tandas; si alguna se cayó hay filas sin monto que NO
                // son "esta cuenta no gastó". Decirlo es la diferencia entre un dato faltante y un
                // dato falso.
                setPartial(Boolean(result?.partial));
                setLoaded(true);

                const refresh = result?.refresh;
                const keepWaiting = Boolean(refresh?.pending) && Date.now() - startedAt < MAX_PREPARE_MS;
                setPreparing(keepWaiting);
                if (!keepWaiting) return;
                const seconds = Number(refresh.retry_after_seconds) || DEFAULT_RETRY_S;
                timer = setTimeout(ask, seconds * 1000);
            });
        };

        setPreparing(true);
        // El GET **no espera** al POST. El GET contesta enseguida por todas las cuentas con lo que ya
        // se sabe —el gasto del mes pasado—, y lo que el POST habilita es la otra fuente, el catálogo,
        // que llega después por el poll. Encadenarlos dejaba la pantalla en «0 de 154 cuentas listas»
        // y sin una sola fila durante más de 40 segundos (medido en el QA del 09/09 sobre la agencia
        // 130), justo mientras el aviso prometía que ya se podía editar.
        //
        // Si el POST falla se sigue igual: el paso muestra lo que haya. Prometer el catálogo y no
        // mostrar nada sería peor que mostrar los montos que sí se pudieron resolver.
        ask();
        prepareBudgets(pks).then(() => { if (alive) ask(); });

        return () => {
            alive = false;
            clearTimeout(timer);
        };
        // Se pide una vez, cuando ya se sabe qué cuentas entran: recalcular con cada tecla del monto
        // volvería a preguntar los presupuestos mientras el usuario los está editando.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, retries]);

    // Las cuentas de Meta, que son las que eligen conversión. Se piden sólo las que todavía no se
    // trajeron: sacar y devolver un cliente no vuelve a salir a Meta por sus cuentas.
    /**
     * Elegir la conversión de las cuentas de Google (C-01), sólo con el backend que la acepta.
     *
     * `add-bulk` viejo rechaza `selected_actions` de Google y el alta entera se caería. El backend que
     * la acepta es el mismo que trae el gasto de 7 días del paso 3 (`rp-be-alta`, un solo deploy), y su
     * marca visible es `spend_refresh` en la propuesta. Sin la marca, la fila queda como antes.
     */
    const googleConversions = useMemo(() => (server.proposals && 'spend_refresh' in server.proposals
        ? {accountActions: server.accountActions || {}, loadAccountActions} : null),
    [server.proposals, server.accountActions, loadAccountActions]);

    // Hay alguna fila de Google con su selector a la vista (para el link «¿Y la conversión?»).
    const choosesGoogle = Boolean(googleConversions)
        && included.some(group => group.accounts.some(entry => choosesGoogleConversion(entry.account)));

    const conversionPks = useMemo(() => included.flatMap(group => group.accounts)
                                                .filter(entry => needsConversion(entry.account))
                                                .map(entry => entry.pk), [included]);
    const [conversionsLoading, setConversionsLoading] = useState(false);
    const missingConversionPks = conversionPks.filter(pk => !server.conversionEvents?.[String(pk)]);
    const missingKey = missingConversionPks.join(',');
    // Las cuentas ya pedidas en esta pantalla. Sin esto, el doble efecto del StrictMode (y cualquier
    // re-render con las mismas cuentas faltantes) volvía a salir a Meta por cada tanda: en el QA del
    // 24/09 cada tanda de 25 salió dos veces. Sólo el reintento las vuelve a pedir.
    const requestedConversions = useRef(new Set());
    const conversionsInFlight = useRef(0);
    // Las cuentas cuyo pedido todavía viaja. El reintento las saltea: sin esto, cada clic en
    // «Reintentar» con el anterior en camino mandaba otra tanda entera a Meta (QA-conv3-04).
    const conversionPksInFlight = useRef(new Set());

    const fetchConversions = useCallback(pks => {
        const pending = pks.filter(pk => !requestedConversions.current.has(pk));
        if (!pending.length) return;
        pending.forEach(pk => {
            requestedConversions.current.add(pk);
            conversionPksInFlight.current.add(pk);
        });
        conversionsInFlight.current += 1;
        setConversionsLoading(true);
        loadConversionEvents(pending).finally(() => {
            pending.forEach(pk => conversionPksInFlight.current.delete(pk));
            conversionsInFlight.current -= 1;
            if (conversionsInFlight.current === 0) setConversionsLoading(false);
        });
    }, [loadConversionEvents]);

    useEffect(() => {
        if (ready) fetchConversions(missingConversionPks);
        // Por el conjunto de cuentas que faltan, no por el array: cambia de identidad en cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, missingKey]);

    /**
     * Vuelve a pedir las cuentas que Meta no contestó, sin recargar la página.
     *
     * Sólo las que tienen una respuesta caída, o ninguna (C-CONVCUENTA-04), y nunca una que ya está
     * en camino, aunque su última respuesta haya sido la caída. Las `NOT_READABLE` no se reintentan
     * nunca: no es una caída, y darían siempre lo mismo.
     */
    const retryConversions = () => {
        const unanswered = conversionPks.filter(pk => {
            if (conversionPksInFlight.current.has(pk)) return false;
            const status = server.conversionEvents?.[String(pk)]?.status;
            return status === EVENTS_UNAVAILABLE || status === undefined;
        });
        unanswered.forEach(pk => requestedConversions.current.delete(pk));
        fetchConversions(unanswered);
    };

    /**
     * Al seguir, la sugerencia pasa a ser la elección. Los eventos viven en la memoria de la pestaña
     * y una recarga en «Creando tu cartera» los perdería: la línea nacería sin conversión aunque el
     * usuario la haya visto propuesta y la haya aceptado.
     */
    const createPortfolio = () => {
        const conversions = {};
        conversionPks.forEach(pk => {
            const {actionIds} = conversionFor({pk, conversionEvents: server.conversionEvents,
                                               conversionEdits: state.conversionEdits});
            if (actionIds.length) conversions[pk] = actionIds;
        });
        dispatch({type: 'CONFIRM_CONVERSIONS', conversions});
        recordPaceChoices(pks, server.budgets, state.budgetEdits);
        // El pedido explícito: sin esto la pantalla de espera no postea (ver `applyPlanFor`).
        dispatch({type: 'APPLY_REQUESTED'});
        navigate('/onboarding/creando');
    };

    const total = pks.length;
    // «Crear N clientes» sólo si los N son nuevos. Con clientes que ya existían (una agencia que ya
    // tenía clientes) el botón prometía crear 21 clientes y lo que se creaba eran 21 líneas sobre
    // clientes de antes (card UxlFOkTh, punto 2). «Empezar a seguir» es cierto para los dos.
    const createKey = included.some(group => group.isNew === false)
        ? 'onboarding.budgets.create_follow' : 'onboarding.budgets.create';
    // El progreso es de cuentas RESUELTAS, no de respuestas recibidas: el GET contesta enseguida
    // por todas y lo que va llegando de a poco es el catálogo. Contar respuestas diría "129 de 129"
    // desde el primer segundo con la pantalla todavía llenándose.
    const resolved = useMemo(() => resolvedBudgetsCountOf(pks, server.budgets), [pks, server.budgets]);
    // Cuántas cuentas van a otro ritmo este mes (C-11). Se dice arriba de la tabla para que elegir el
    // monto de esas filas sea una decisión y no un aviso al pie que se saltea (fserb-12, 15/10).
    const paceCount = useMemo(() => paceNoticeCountOf(pks, server.budgets, state.budgetEdits),
                              [pks, server.budgets, state.budgetEdits]);

    // Llegar sin ningún cliente es el camino NORMAL desde que el paso 3 no pre-tilda nada: se entra
    // por el stepper o por un enlace directo y no hay nada que presupuestar. Sin esto la pantalla
    // prometía «dejamos 0 clientes listos», explicaba cómo leer montos que no existen y ofrecía un
    // botón muerto, sin decir en ningún lado que faltaba elegir en el paso anterior.
    const isEmpty = loaded && !included.length;

    /**
     * Sacar un cliente desde acá, sin volver al paso anterior.
     *
     * Reusa el mismo `dismissed` del cajón del paso 3 en vez de un estado propio: es la misma
     * decisión —esta cuenta no produce línea— y duplicarla dejaría dos fuentes discrepando sobre
     * qué se va a crear, que es justo lo que el botón de abajo promete. El deshacer devuelve la
     * fila entera porque `dismissChange` guarda el tilde que tenía antes.
     */
    const dismissGroup = group => {
        setJustDismissed(group.name);
        dispatch({type: 'SET_DISMISSED_MANY', dismissed: true, at: DISMISSED_AT,
                  pks: group.accounts.map(entry => entry.pk)});
    };

    const restore = entry => {
        dispatch({type: 'SET_DISMISSED_MANY', dismissed: false, pks: entry.pks});
    };

    // Hasta saber qué cuentas entran no hay ni denominador que mostrar. Se dice qué se está
    // esperando: se llega acá recargando o por enlace directo, o sea sin nada en memoria, y es la
    // misma consulta cara del paso 3 — un spinner solo no distingue eso de una pantalla colgada.
    if (!ready) return <ObLoading titleKey="onboarding.loading.budgets"/>;

    return (
        <div className="ob-step-body -wide">
            <header className="ob-head">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.budgets.title')}</h1>
                {/* Una frase y tres viñetas (H-02, recorrido con Pablo del 13/10). Eran cuatro bloques
                    —unas 200 palabras— y al llegar al paso no se veía ninguna fila: «Este choclo así
                    no te lo lee nadie. Y lo que va a lograr es que nadie lea el que sí es
                    importante». Cómo se elige la conversión y cómo se suman las monedas es el
                    procedimiento, no lo que hace falta para seguir: va detrás del link a la ayuda.
                    Con el error en pantalla o sin clientes no se muestra: explicar montos que no
                    llegaron tapaba lo único que importaba en esa pantalla (QA-VIS-journey-09). */}
                {!failed && !isEmpty && (
                    <>
                        <p className="-lead" {...CLARITY_UNMASK}>{t('onboarding.budgets.subtitle')}</p>
                        {/* Con HTML por el rótulo en negrita de cada viñeta; las claves no
                            interpolan nada. La de la conversión, sólo si alguna cuenta la elige: sin
                            cuentas de Meta, «proponemos una por cuenta» sería falso. */}
                        <ul className="ob-head__points">
                            {conversionPks.length > 0 && (
                                <li {...CLARITY_UNMASK} dangerouslySetInnerHTML={{__html: t('onboarding.budgets.point_conversion')}}/>
                            )}
                            <li {...CLARITY_UNMASK} dangerouslySetInnerHTML={{__html: t('onboarding.budgets.point_currency')}}/>
                            <li {...CLARITY_UNMASK} dangerouslySetInnerHTML={{__html: t('onboarding.budgets.point_goals')}}/>
                        </ul>
                        <p className="ob-head__help">
                            <HelpLink href={HELP_BUDGETS_URL}>{t('onboarding.budgets.how_we_calculate')}</HelpLink>
                            {(conversionPks.length > 0 || choosesGoogle) && (
                                <>
                                    <span className="ob-head__help-sep" aria-hidden="true">·</span>
                                    <HelpLink href={HELP_CONVERSION_URL}>
                                        {t('onboarding.budgets.how_we_pick_conversion')}
                                    </HelpLink>
                                </>
                            )}
                        </p>
                    </>
                )}
            </header>

            {partial && (
                <p className="ob-apply-error__reason" role="status">
                    {t('onboarding.budgets.partial')}
                </p>
            )}

            {/* El aviso dice DOS cosas y las dos hacían falta. Cuántas cuentas ya están resueltas,
                porque un spinner sin progreso no distingue "faltan tres segundos" de "esto se
                colgó"; y que no hay que esperar para tocar, porque sin decirlo el usuario no sabe
                si le toca a él — pasó en la pasada del 07/09: se puso a editar creyendo que la
                pantalla lo estaba esperando. Lo que edite gana sobre lo que llegue después. */}
            {/* Un error no puede pintarse como un dato. Sin esto, la caída de las dos tandas se
                veía como una cartera entera «sin presupuesto» y el poll moría en silencio. */}
            {failed && (
                <p className="ob-apply-error__reason" role="alert">
                    {t('onboarding.budgets.failed')}
                    {' '}
                    <button type="button" className="ob-link"
                            onClick={() => { setRetries(retries + 1); retryConversions(); }}>
                        {t('onboarding.budgets.retry')}
                    </button>
                </p>
            )}

            {!failed && (preparing || !loaded) && (
                <p className="ob-note" role="status">
                    <span className="ob-spinner-mini"/>
                    {/* `count` y no `total`: i18next elige singular/plural por `count`, y sin eso
                        una agencia con una sola cuenta lee «1 de 1 cuentas listas». */}
                    {' '}{t('onboarding.budgets.preparing', {done: resolved, count: total})}
                </p>
            )}

            {/* Sin clientes no hay nada que presupuestar: se dice, y se ofrece el camino de vuelta.
                El botón del pie ya nace deshabilitado, pero un botón muerto no explica qué falta. */}
            {!failed && isEmpty && (
                <div className="ob-empty-step">
                    <p className="ob-empty-step__title">{t('onboarding.budgets.empty_title')}</p>
                    <p className="ob-empty-step__body">{t('onboarding.budgets.empty_body')}</p>
                    <button type="button" className="ob-btn -primary" {...CLARITY_UNMASK}
                            onClick={() => navigate('/onboarding/clientes')}>
                        {t('onboarding.budgets.empty_cta')}
                    </button>
                </div>
            )}

            {!failed && loaded && paceCount > 0 && (
                <p className="ob-pace-summary" role="status">
                    {t('onboarding.budgets.pace_summary', {count: paceCount})}
                </p>
            )}

            {failed ? null : loaded ? (
                <div className="ob-budgets">
                    {/* Qué es cada número de la fila, dicho una vez arriba. Sin esto el monto se
                        leía como un dato más y no como el presupuesto que se está aprobando: Leo,
                        frente a «gastó 38.063 → 39.000 / mes», pidió «un título en la columna, que
                        sea bien claro» (card C9zl8x1e). Mismas pistas que `.ob-budget__row`, así
                        que cada rótulo cae sobre su columna; en angosto la fila se apila y el
                        encabezado se va (ver el SCSS). */}
                    {included.length > 0 && (
                        <div className="ob-budget-head">
                            <span>{t('onboarding.budgets.col_client')}</span>
                            <span>{t('onboarding.budgets.col_spent')}</span>
                            <span/>
                            <span className="-amount">{t('onboarding.budgets.col_budget')}</span>
                            <span/>
                        </div>
                    )}
                    {/* Lo descartado no desaparece sin dejar rastro: la fila se va —que es el punto, no
                        estorbar entre cientos— y en su LUGAR queda nombrada con su deshacer (H-03). Al
                        final de la tabla, donde estaba, quedaba fuera de la vista desde donde se hizo
                        el clic. */}
                    {rows.map(row => (row.kind === 'group' ? (
                        <BudgetRow key={row.group.key} group={row.group} budgets={server.budgets} state={state}
                                   dispatch={dispatch} preparing={preparing}
                                   canEditCurrency={Boolean(agency?.can_edit_currency)}
                                   onDismiss={() => dismissGroup(row.group)}
                                   conversionEvents={server.conversionEvents}
                                   conversionsLoading={conversionsLoading}
                                   onRetryConversions={retryConversions}
                                   googleConversions={googleConversions}/>
                    ) : (
                        <div className="ob-budget-undo" role="status" key={`undo:${row.entry.key}`}>
                            {t('onboarding.budgets.dismissed', {client: row.entry.name,
                                                                interpolation: {escapeValue: false}})}
                            {/* El foco viene acá al sacar el cliente: el botón que se apretó ya no
                                existe, y sin esto quien usa el teclado queda al principio de la
                                página. Sólo con el recién sacado, no con los que vuelven de una
                                recarga. */}
                            <button type="button" className="ob-link" onClick={() => restore(row.entry)}
                                    autoFocus={row.entry.key === justDismissed}>
                                <ArrowUUpLeft size={13}/> {t('onboarding.budgets.restore')}
                            </button>
                        </div>
                    )))}
                </div>
            ) : (
                <div className="box-loader">
                    <Spinner color="positive"/>
                </div>
            )}

            {/* Pegado abajo (`-sticky`), como el del paso 3: a 1440×812 «Crear 7 clientes…» quedaba
                debajo del pliegue y Leo tuvo que scrollear para encontrarlo (card C9zl8x1e). Es la
                única salida hacia adelante del paso. Sin clientes el pie queda vacío, y una franja
                blanca con sombra pegada abajo no tendría nada que sostener. */}
            <footer className={`ob-foot${isEmpty ? '' : ' -sticky'}`}>
                {/* Deshabilitado hasta que los montos estén: el paso siguiente postea `add-bulk`
                    apenas monta, con lo que haya en `server.budgets`. Con la primera respuesta
                    todavía en camino eso está VACÍO, así que apretar acá crea la cartera entera sin
                    un solo presupuesto — justo lo que este paso existe para evitar. Antes el footer
                    entero vivía detrás de `loaded`; al sacar las filas del gate para poder mostrar
                    el progreso, el botón se quedó afuera del candado. */}
                {/* Sin clientes el pie no dibuja nada: el bloque vacío de arriba ya tiene el único
                    botón que sirve ahí, y «Crear 0 clientes» deshabilitado al lado sería una
                    segunda acción muerta compitiendo con él. */}
                {!isEmpty && <QuotaNote quota={quota}/>}
                {!isEmpty && (
                    <button type="button" className="ob-btn -primary"
                            disabled={!loaded || !included.length || Boolean(quota && quota.over > 0)}
                            onClick={createPortfolio}>
                        {t(createKey, {count: included.length})}
                    </button>
                )}
            </footer>
        </div>
    );
}

/**
 * La medición de UD-22: con qué monto arrancaron las cuentas que tenían el aviso de ritmo. Una vez
 * por cuenta, al crear: lo que cuenta es la elección final, no los clics de ida y vuelta.
 */
function recordPaceChoices(pks, budgets, budgetEdits) {
    const {withNotice, choseThisMonth, ownAmount} = paceChoicesOf(pks, budgets, budgetEdits);
    for (let i = 0; i < withNotice; i += 1) ScreenTracker.recordEvent('onboarding_pace_account');
    for (let i = 0; i < choseThisMonth; i += 1) ScreenTracker.recordEvent('onboarding_pace_this_month');
    for (let i = 0; i < ownAmount; i += 1) ScreenTracker.recordEvent('onboarding_pace_own_amount');
}

/**
 * Un link a un artículo del Centro de Ayuda, en otra pestaña: el usuario está en la mitad del alta.
 *
 * Con la atribución de siempre (quién lo abre y desde qué pantalla): un link a la ayuda que no la
 * usa no se cuenta. El token se pide al montarse, como hace `LinkToArticle`.
 */
function HelpLink({href, children}) {
    useEffect(() => { prefetchHelpCenterIdentity(); }, []);
    return (
        <a className="ob-link" href={href} data-help-url={href} target="_blank" rel="noopener noreferrer"
           onMouseDown={attributeHelpCenterClick} onClick={attributeHelpCenterClick}>
            {children}
        </a>
    );
}
